import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, PhoneCall, Phone } from "lucide-react";
import BrandLogo from "@/components/site/BrandLogo";

const links = [
  { to: "/", label: "Home" },
  { to: "/dialer", label: "Dialer" },
  { to: "/rates", label: "Rates" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact-us", label: "Contact" },
];

export default function NavBar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-brand-line/80 shadow-[0_4px_20px_-12px_rgba(7,27,77,0.18)]"
          : "bg-white/0 border-b border-transparent"
      }`}
    >
      <div className="container-page flex items-center justify-between h-16 sm:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <BrandLogo variant="wordmark" className="h-9 sm:h-10 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-sm font-semibold transition-colors py-1.5 ${
                  isActive
                    ? "text-brand-ink"
                    : "text-brand-ink/70 hover:text-brand-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute left-0 right-0 -bottom-0.5 h-0.5 rounded-full bg-brand-green transition-opacity ${
                      isActive ? "opacity-100" : "opacity-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+441619894842"
            data-testid="nav-phone"
            className="hidden xl:inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/80 hover:text-brand-ink"
          >
            <PhoneCall className="h-4 w-4" />
            +44 161 989 4842
          </a>
          <Link
            to="/dialer"
            data-testid="nav-start-calling"
            className="inline-flex items-center gap-1.5 rounded-full border border-brand-green text-brand-greenDark hover:bg-brand-greenSoft px-4 py-2 text-sm font-semibold"
          >
            <Phone className="h-3.5 w-3.5 fill-current" /> Start Calling
          </Link>
          <Link
            to="/login"
            data-testid="nav-signin"
            className="inline-flex items-center text-sm font-semibold text-brand-ink/80 hover:text-brand-ink"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            data-testid="nav-get-started"
            className="inline-flex items-center gap-2 rounded-full bg-brand-green px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-greenHover transition-colors"
          >
            Get Started
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-xl border border-brand-line bg-white"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-brand-line bg-white" data-testid="nav-mobile-drawer">
          <div className="container-page py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg text-sm font-semibold ${
                    isActive
                      ? "bg-brand-ink text-white"
                      : "text-brand-ink hover:bg-brand-bgSoft"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/dialer"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-start-calling"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-brand-green text-brand-greenDark hover:bg-brand-greenSoft px-4 py-3 text-sm font-semibold"
            >
              <Phone className="h-4 w-4 fill-current" /> Start Calling
            </Link>
            <Link
              to="/register"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-get-started"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green px-4 py-3 text-sm font-semibold text-white hover:bg-brand-greenHover"
            >
              Get Started — it’s free
            </Link>
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-signin"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-line px-4 py-3 text-sm font-semibold text-brand-ink hover:bg-brand-bgSoft"
            >
              Sign in
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
